import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, setToken, removeToken } from '../utils/auth';
import axios from '../api/axiosInstance';

interface Company {
  id: number;
  name: string;
  brand_name: string;
  is_active: boolean;
  address?: string;
  contact_number?: string;
  email?: string;
}

// Define Permission interface
interface Permission {
  module: string;
  can_view: boolean;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface User {
  id: number;
  email: string;
  name: string;
  role?: 'MASTER' | 'USER';
  company?: Company;
  // Allow permissions to be either an array of objects or an array of strings for backward compatibility
  permissions?: Permission[] | string[] | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (token: string) => void;
  logout: () => void;
  hasPermission: (module: string, action: 'view' | 'add' | 'edit' | 'delete') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to safely check if permissions is an array of strings
const isStringArray = (arr: any[]): arr is string[] => {
  return arr.length > 0 ? typeof arr[0] === 'string' : true;
};

// Helper function to safely check if permissions is an array of Permission objects
const isPermissionArray = (arr: any[]): arr is Permission[] => {
  return arr.length > 0 ? 
    typeof arr[0] === 'object' && 
    arr[0] !== null &&
    'module' in arr[0] && 
    'can_view' in arr[0] : true;
};

// Helper function to validate user data structure
const isValidUser = (userData: any): userData is User => {
  return (
    userData && 
    typeof userData === 'object' &&
    'id' in userData &&
    'email' in userData &&
    'name' in userData
  );
};

// Helper function to normalize module names for consistent checking
// This should match the same function in usePermissions.ts
const normalizeModuleName = (module: string): string => {
  // Convert module name to lowercase and replace hyphens with underscores
  return module.toLowerCase().replace(/[-_]/g, '_');
};

// Helper function to ensure permission module names are consistent
const normalizePermissions = (permissions: any[]): any[] => {
  if (!Array.isArray(permissions)) return [];
  
  if (permissions.length === 0) return [];
  
  // Handle string array permissions
  if (typeof permissions[0] === 'string') {
    return permissions.map(p => p.toLowerCase().replace(/[-_]/g, '_'));
  }
  
  // Handle object permissions
  if (typeof permissions[0] === 'object' && permissions[0] !== null && 'module' in permissions[0]) {
    return permissions.map(p => ({
      ...p,
      module: normalizeModuleName(p.module)
    }));
  }
  
  return permissions;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    if (token) {
      // Validate token and check user status
      axios.get('/auth/validate')
        .then(response => {
          const userData = response.data;
          if (userData && userData.is_active) {
            setIsAuthenticated(true);
            setUser(userData);
          } else {
            // If user is inactive, clear token and redirect to login
            removeToken();
            navigate('/login');
          }
        })
        .catch((error) => {
          console.error('Token validation error:', error);
          removeToken();
          navigate('/login');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [navigate]);

  const login = (token: string) => {
    setToken(token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    removeToken();
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  };

  const hasPermission = (module: string, action: 'view' | 'add' | 'edit' | 'delete'): boolean => {
    console.log(`Checking permission: ${module}.${action}`);
    
    // If no user, definitely no permission
    if (!user) {
      console.log('No user - permission denied');
      return false;
    }

    // Note: MASTER role users also need explicit permissions - no automatic access

    // No permissions array
    if (!user.permissions) {
      console.log('User has no permissions array - permission denied');
      return false;
    }

    // Normalize the module name for checking
    const normalizedModule = normalizeModuleName(module);
    console.log(`Normalized module for permission check: ${module} -> ${normalizedModule}`);

    // Check if permissions is a string array (old format)
    if (isStringArray(user.permissions)) {
      // Check for match with normalized module names
      const result = user.permissions.some(p => normalizeModuleName(p) === normalizedModule);
      console.log(`String-based permission check: ${result ? 'granted' : 'denied'}`);
      return result;
    }

    // Check if permissions is an object array (new format)
    if (isPermissionArray(user.permissions)) {
      // Find permission by matching normalized module names
      const permission = user.permissions.find(p => normalizeModuleName(p.module) === normalizedModule);
      
      if (!permission) {
        console.log(`No permission found for module ${module} (normalized: ${normalizedModule}) - permission denied`);
        if (user.permissions.length > 0) {
          console.log('Available modules:', user.permissions.map(p => p.module).join(', '));
        }
        return false;
      }
      
      let hasPermission = false;
      
      switch (action) {
        case 'view':
          hasPermission = permission.can_view;
          break;
        case 'add':
          hasPermission = permission.can_add;
          break;
        case 'edit':
          hasPermission = permission.can_edit;
          break;
        case 'delete':
          hasPermission = permission.can_delete;
          break;
      }
      
      console.log(`Object-based permission check for ${module}.${action}: ${hasPermission ? 'granted' : 'denied'}`);
      return hasPermission;
    }

    // If we get here, the permissions data structure is not recognized
    console.warn('Unrecognized permission format - permission denied');
    return false;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 